import { Components, type LevaInputProps, createPlugin, useInputContext } from 'leva/plugin';
import { useMemo } from 'react';

const { Label, Row } = Components;

type ValueType = string[];

type MultiSelectType = {
  input: {
    value: string[];
    options: string[];
    config: {
      type?: 'select' | 'chips';
      selectAll?: string;
      selectNone?: string;
    };
  };
};

type InternalSettingsType = Omit<MultiSelectType['input'], 'value'>;

type MultiSelectPropsType = LevaInputProps<ValueType, InternalSettingsType>;

const sanitizeValue = (value: ValueType, settings: InternalSettingsType): ValueType => {
  if (!Array.isArray(value)) {
    return [];
  }

  // let finalValue = value ?? [];
  // if (settings?.config?.selectAll && finalValue.includes(settings?.config?.selectAll)) {
  //   finalValue = [settings?.config?.selectAll];
  // } else if (settings?.config?.selectNone && finalValue.includes(settings?.config?.selectNone)) {
  //   finalValue = [settings?.config?.selectNone];
  // }

  return value;
};

const plugin = createPlugin<MultiSelectType, ValueType, InternalSettingsType>({
  sanitize: (value: ValueType, settings, prevValue, path, store) => {
    return sanitizeValue(value, settings);
  },
  format: (value: ValueType, settings) => {
    return {
      value: sanitizeValue(value, settings),
    };
  },
  normalize: (input: MultiSelectType, path, data) => {
    const { value, ...settings } = input.input;

    return {
      value: value ?? [],
      settings: settings,
    };
  },
  component: Component,
});

function Component() {
  const { label, value, onUpdate, settings } = useInputContext<MultiSelectPropsType>();

  const { displayType, selectAll, selectNone, options } = useMemo(() => {
    const type = settings?.config?.type ?? 'select';

    const options = [...(settings?.options ?? [])].toSorted((a, b) => {
      // sort by selectedAll and selectedNone first
      if (!!settings?.config?.selectAll && a === settings?.config?.selectAll) {
        return -1;
      }
      if (!!settings?.config?.selectNone && a === settings?.config?.selectNone) {
        return -1;
      }

      return 0;
    });

    if (!!settings?.config?.selectAll && !options.includes(settings?.config?.selectAll)) {
      options.unshift(settings?.config?.selectAll);
    }
    if (!!settings?.config?.selectNone && !options.includes(settings?.config?.selectNone)) {
      options.unshift(settings?.config?.selectNone);
    }

    return {
      displayType: type,
      selectAll: settings?.config?.selectAll ?? undefined,
      selectNone: settings?.config?.selectNone ?? undefined,
      options: [...new Set(options)],
    };
  }, [settings]);

  return (
    <Row input>
      <Label>{label}</Label>
      <div>
        {/* show chips */}
        {displayType === 'chips' &&
          (options ?? []).map((chip) => {
            return (
              <div
                key={chip}
                style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  margin: '4px',
                  borderRadius: '4px',
                  backgroundColor: value?.includes(chip) ? '#007bff' : '#e0e0e0',
                  color: value?.includes(chip) ? '#fff' : '#000',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (chip === selectAll) {
                    // update all values except the selectNone
                    const newValues = options.filter((v) => {
                      if (v === selectNone) {
                        return false;
                      }
                      return true;
                    });
                    onUpdate(newValues);
                    return;
                  }

                  if (chip === selectNone) {
                    // deselect all values except the selectAll
                    const newValues = options.filter((v) => {
                      if (v === selectNone) {
                        return true;
                      }
                      return false;
                    });
                    onUpdate(newValues);
                    return;
                  }

                  const newValues = value?.includes(chip) ? value.filter((v) => v !== chip) : [...(value ?? []), chip];

                  onUpdate(newValues.filter((v) => v !== selectNone && v !== selectAll));
                }}
              >
                {chip}
              </div>
            );
          })}
      </div>
    </Row>
  );
}

export default plugin;
